// US-072 — AI scoring banner.
//
// Renders the business chips (or "Hors cible" placeholder), the
// temperature pill, and an "EN PAUSE" badge when a pending action blocks
// the conv. Mirrors `prototypes_and_docs/ls-inbox.jsx:392-469`.
//
// Wave B2: chips render real data from `conv.businesses` (top-3 by affinity
// score, fed from `GET /conversations/:id`). When the entry carries a
// `score`, we surface it through opacity + an aria-label so screen-reader
// users hear "Business {name}, score {score}%".
//
// We use DS primitives (Pill + LeadTemperaturePill) — no inline colour
// tokens. Layout-only inline styles per the wave hard rules (DS composite-
// or component-only for visuals).

import { useT } from '@barel/i18n';
import { LeadTemperaturePill, Pill } from '@barel/ui';

import type { ConversationView } from './types';

export interface AIScoringBannerProps {
  conv: Pick<ConversationView, 'businesses' | 'temperature' | 'pendingAction'>;
}

interface BusinessChipData {
  name: string;
  score?: number;
  id?: string;
}

function normaliseBusinesses(input: ConversationView['businesses']): BusinessChipData[] {
  if (!input) return [];
  return input
    .map((entry) =>
      typeof entry === 'string'
        ? { name: entry }
        : { name: entry.name, score: entry.score, id: entry.id },
    )
    .filter((b) => b.name.length > 0)
    .slice(0, 3);
}

export function AIScoringBanner({ conv }: AIScoringBannerProps) {
  const t = useT('inbox');
  const businesses = normaliseBusinesses(conv.businesses);
  const isPending = !!conv.pendingAction;

  return (
    <div
      data-ls-region="ai-scoring-banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-4)',
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--bg-base)',
        flexWrap: 'wrap',
        minHeight: 38,
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-2xs)',
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {t('thread.scoring.businessLabel')}
      </span>

      {businesses.length === 0 ? (
        <Pill tone="neutral" variant="dashed" size="xs">
          {`🚫 ${t('thread.scoring.horsCible')}`}
        </Pill>
      ) : (
        businesses.map((biz, i) => {
          // Score-based opacity — clamp to [0.55, 1.0] so a low-confidence
          // chip is still legible. Chips without a score render full opacity.
          const opacity =
            typeof biz.score === 'number' ? Math.max(0.55, Math.min(1, biz.score)) : 1;
          const ariaLabel =
            typeof biz.score === 'number'
              ? t('business.affinity_chip_aria_label', {
                  name: biz.name,
                  score: Math.round(biz.score * 100),
                })
              : undefined;
          return (
            <span
              key={biz.id ?? `${biz.name}-${i}`}
              style={{ opacity }}
              data-business-id={biz.id}
              data-business-score={
                typeof biz.score === 'number' ? Math.round(biz.score * 100) : undefined
              }
              aria-label={ariaLabel}
            >
              <Pill tone="neutral" variant="soft" size="xs">
                {biz.name}
              </Pill>
            </span>
          );
        })
      )}

      <span style={{ flex: '1 1 auto' }} />

      {conv.temperature ? (
        <span title={t('thread.scoring.temperatureTooltip')}>
          <LeadTemperaturePill temperature={conv.temperature} size="xs">
            {temperatureLabel(t, conv.temperature)}
          </LeadTemperaturePill>
        </span>
      ) : null}

      {isPending ? (
        <span title={t('thread.scoring.enPauseTooltip')}>
          <Pill tone="magenta" variant="soft" size="xs" uppercase>
            {t('thread.scoring.enPause')}
          </Pill>
        </span>
      ) : null}
    </div>
  );
}

function temperatureLabel(
  t: (key: string) => string,
  temperature: NonNullable<ConversationView['temperature']>,
): string {
  switch (temperature) {
    case 'cold':
      return `❄️ ${t('thread.scoring.temperatureCold')}`;
    case 'warm':
      return `☀️ ${t('thread.scoring.temperatureWarm')}`;
    case 'hot':
      return `🔥 ${t('thread.scoring.temperatureHot')}`;
    default:
      return temperature;
  }
}
